import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Save, FileText, Info, AlertCircle, Car } from 'lucide-react';
import { toast } from 'sonner';
import { HostAppShell } from '../components/HostAppShell';

// FIX 2: Numeric input component that allows natural typing
const NumericInput = ({ value, onChange, placeholder = "0", step = "1", min, className = "", ...props }) => {
  const [displayValue, setDisplayValue] = useState(value?.toString() || '');
  
  useEffect(() => {
    // Only update display if external value changes significantly
    if (value !== undefined && value !== null) {
      const numValue = parseFloat(displayValue) || 0;
      if (Math.abs(numValue - value) > 0.001 || displayValue === '') {
        setDisplayValue(value === 0 ? '' : value.toString());
      }
    }
  }, [value]);
  
  const handleChange = (e) => {
    const inputValue = e.target.value;
    setDisplayValue(inputValue);
    
    // Parse and send to parent
    const parsed = parseFloat(inputValue);
    onChange(isNaN(parsed) ? 0 : parsed);
  };
  
  const handleBlur = () => {
    // On blur, if empty, show placeholder behavior but keep 0 as value
    if (displayValue === '' || displayValue === undefined) {
      onChange(0);
    }
  };
  
  const handleFocus = (e) => {
    // Select all text on focus for easy replacement
    e.target.select();
  };
  
  return (
    <Input
      type="number"
      step={step}
      min={min}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      placeholder={placeholder}
      className={className}
      {...props}
    />
  );
};

const HostRulesPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showBulkApplyModal, setShowBulkApplyModal] = useState(false);
  const [applyToAll, setApplyToAll] = useState(false);
  const [rules, setRules] = useState({
    smoking_allowed: false,
    cleaning_required: true,
    detailing_fee: 0,
    fuel_return_policy: 'same_level',
    late_return_fee_per_hour: 50,
    security_bond: 500,
    excess_km_fee: 0.35,
    kms_included: 200,
    pets_allowed: false,
    minimum_driver_age: 21,
    licence_restrictions: '',
    custom_rules: ''
  });

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      const response = await api.get('/users/owner-rules');
      setRules(response.data);
    } catch (error) {
      console.error('Error fetching rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowBulkApplyModal(true);
  };

  const handleSaveWithBulkApply = async () => {
    setSaving(true);
    try {
      await api.put('/users/owner-rules', rules);
      
      if (applyToAll) {
        const bulkResponse = await api.post('/users/host-rules/bulk-apply', {
          rules: rules,
          apply_to_all: true
        });
        toast.success(`Rules saved and applied to ${bulkResponse.data.updated_count} vehicles`);
      } else {
        toast.success('Default rules saved. New vehicles will use these settings.');
      }
      
      setShowBulkApplyModal(false);
      setApplyToAll(false);
    } catch (error) {
      toast.error('Failed to update rules');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <HostAppShell title="Host Rules" activeId="rules">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004B8D]"></div>
        </div>
      </HostAppShell>
    );
  }

  return (
    <HostAppShell title="Host Rules" activeId="rules">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Default Template Info Banner */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-800">Default Template for All Vehicles</p>
              <p className="text-sm text-blue-700 mt-1">
                These settings act as default rules for new vehicles. Individual vehicles can override 
                these values in their vehicle settings. When you save changes, you can choose to apply 
                them to existing vehicles as well.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Vehicle Rules */}
          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#004B8D]" />
                Vehicle Rules
              </CardTitle>
              <CardDescription>
                Set default rules for all your vehicles.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Smoking Allowed</Label>
                  <p className="text-sm text-slate-500">Allow smoking in the vehicle</p>
                </div>
                <Switch
                  checked={rules.smoking_allowed}
                  onCheckedChange={(checked) => setRules({ ...rules, smoking_allowed: checked })}
                  data-testid="smoking-toggle"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Pets Allowed</Label>
                  <p className="text-sm text-slate-500">Allow pets in the vehicle</p>
                </div>
                <Switch
                  checked={rules.pets_allowed}
                  onCheckedChange={(checked) => setRules({ ...rules, pets_allowed: checked })}
                  data-testid="pets-toggle"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Cleaning Required</Label>
                  <p className="text-sm text-slate-500">Vehicle must be returned clean</p>
                </div>
                <Switch
                  checked={rules.cleaning_required}
                  onCheckedChange={(checked) => setRules({ ...rules, cleaning_required: checked })}
                  data-testid="cleaning-toggle"
                />
              </div>

              {rules.cleaning_required && (
                <div>
                  <Label htmlFor="detailing_fee">Detailing Fee ($)</Label>
                  <NumericInput
                    id="detailing_fee"
                    step="0.01"
                    value={rules.detailing_fee}
                    onChange={(val) => setRules({ ...rules, detailing_fee: val })}
                    placeholder="0"
                    className="mt-1"
                    data-testid="detailing-fee-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Fee charged if vehicle returned dirty</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Fuel & Mileage */}
          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle className="font-heading">Fuel & Mileage</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Fuel Return Policy</Label>
                <Select
                  value={rules.fuel_return_policy}
                  onValueChange={(value) => setRules({ ...rules, fuel_return_policy: value })}
                >
                  <SelectTrigger className="mt-1" data-testid="fuel-policy-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="same_level">Return at same level</SelectItem>
                    <SelectItem value="full">Return full tank</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="kms_included">KMs Included per Day</Label>
                  <NumericInput
                    id="kms_included"
                    value={rules.kms_included}
                    onChange={(val) => setRules({ ...rules, kms_included: Math.floor(val) })}
                    placeholder="200"
                    className="mt-1"
                    data-testid="kms-included-input"
                  />
                </div>
                <div>
                  <Label htmlFor="excess_km_fee">Excess KM Fee ($)</Label>
                  <NumericInput
                    id="excess_km_fee"
                    step="0.01"
                    value={rules.excess_km_fee}
                    onChange={(val) => setRules({ ...rules, excess_km_fee: val })}
                    placeholder="0.35"
                    className="mt-1"
                    data-testid="excess-km-fee-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Fees & Policies */}
          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle className="font-heading">Fees & Policies</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="late_fee">Late Return Fee ($/hour)</Label>
                <NumericInput
                  id="late_fee"
                  step="0.01"
                  value={rules.late_return_fee_per_hour}
                  onChange={(val) => setRules({ ...rules, late_return_fee_per_hour: val })}
                  placeholder="50"
                  className="mt-1"
                  data-testid="late-fee-input"
                />
                <p className="text-xs text-slate-500 mt-1">Fee charged per hour for late returns</p>
              </div>
              <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>Note:</strong> Security bond amounts are configured per-vehicle in the vehicle settings.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Driver Requirements */}
          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle className="font-heading">Driver Requirements</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="min_age">Minimum Driver Age</Label>
                <NumericInput
                  id="min_age"
                  value={rules.minimum_driver_age}
                  onChange={(val) => setRules({ ...rules, minimum_driver_age: Math.floor(val) || 18 })}
                  placeholder="21"
                  min={18}
                  className="mt-1"
                  data-testid="min-age-input"
                />
              </div>

              <div>
                <Label htmlFor="licence_restrictions">Licence Restrictions</Label>
                <Input
                  id="licence_restrictions"
                  value={rules.licence_restrictions}
                  onChange={(e) => setRules({ ...rules, licence_restrictions: e.target.value })}
                  placeholder="e.g., Full licence required, no P plates"
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>

          {/* Custom Rules */}
          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle className="font-heading">Additional Rules</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={rules.custom_rules}
                onChange={(e) => setRules({ ...rules, custom_rules: e.target.value })}
                placeholder="Add any custom rules or policies for your vehicles..."
                rows={4}
              />
            </CardContent>
          </Card>

          <Button 
            type="submit" 
            className="w-full bg-[#004B8D] hover:bg-[#003865] h-12"
            disabled={saving}
            data-testid="save-rules-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Rules & Policies'}
          </Button>
        </form>
      </div>

      {/* Bulk Apply Modal */}
      <Dialog open={showBulkApplyModal} onOpenChange={setShowBulkApplyModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Car className="w-5 h-5 text-[#004B8D]" />
              Apply Changes to Vehicles
            </DialogTitle>
            <DialogDescription>
              Choose how to apply your updated rules
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div 
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                !applyToAll ? 'border-[#004B8D] bg-blue-50' : 'border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => setApplyToAll(false)}
            >
              <div className="flex items-start gap-3">
                <Checkbox checked={!applyToAll} className="mt-0.5" />
                <div>
                  <p className="font-medium text-sm">Only apply to new vehicles</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Existing vehicles will keep their current settings. New vehicles will use these defaults.
                  </p>
                </div>
              </div>
            </div>

            <div 
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                applyToAll ? 'border-[#004B8D] bg-blue-50' : 'border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => setApplyToAll(true)}
            >
              <div className="flex items-start gap-3">
                <Checkbox checked={applyToAll} className="mt-0.5" />
                <div>
                  <p className="font-medium text-sm">Apply to all vehicles</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Update all vehicles that haven't enabled custom overrides. Vehicles with custom values will not be changed.
                  </p>
                </div>
              </div>
            </div>

            {applyToAll && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700">
                    Only vehicles without custom overrides will be updated. Vehicles with individual settings will retain their values.
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowBulkApplyModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveWithBulkApply}
              disabled={saving}
              className="bg-[#004B8D] hover:bg-[#003865]"
              data-testid="confirm-save-rules"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </HostAppShell>
  );
};

export default HostRulesPage;
